import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { AlertTriangle, ShieldCheck, Loader2, RefreshCw, Info } from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────────
// Stabilisation Blocker #2 — Funding Preview Banner
//
// Read-only soft warning surface inside the Timesheet Approval drawer.
// Never blocks an approval; never mutates anything. Pairs with the
// existing backend hard-block on `funding_plans.categories.remaining`
// inside POST /timesheets/{id}/approve, which is unchanged.
//
// Backend: GET /api/timesheets/{id}/funding-preview
// Permission: timesheets.approve  (Admin / Coordinator / Finance)
//
// States rendered:
//   - loading  → small skeleton row
//   - ok       → tiny green pill ("Funding healthy")
//   - warning  → AMBER banner with category, balance, projected, after
//   - perm     → muted line ("You don't have permission to preview funding")
//   - error    → muted line with retry
//   - skipped  → renders nothing when no participant_id / no estimate
//
// ──────────────────────────────────────────────────────────────────────────

const fmtMoney = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(n));
};

const fmtPct = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toFixed(1)}%`;
};

export default function FundingPreviewBanner({ timesheetId }) {
  const [state, setState] = useState({ loading: true, data: null, error: null, perm: false });

  const fetchPreview = useCallback(async () => {
    if (!timesheetId) return;
    setState((s) => ({ ...s, loading: true, error: null, perm: false }));
    try {
      const r = await api.get(`/timesheets/${timesheetId}/funding-preview`);
      setState({ loading: false, data: r.data?.data || null, error: null, perm: false });
    } catch (e) {
      const status = e?.response?.status;
      const errBody = e?.response?.data;
      const code = errBody?.error?.code
        || errBody?.detail?.error?.code
        || errBody?.detail?.code;
      if (status === 403 && code !== 'FEATURE_DISABLED') {
        setState({ loading: false, data: null, error: null, perm: true });
      } else {
        setState({
          loading: false,
          data: null,
          error: errBody?.error?.message || e?.message || 'Funding preview unavailable',
          perm: false,
        });
      }
    }
  }, [timesheetId]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const { loading, data, error, perm } = state;

  if (loading) {
    return (
      <div
        className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 flex items-center gap-2"
        data-testid="funding-preview-loading"
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading funding preview…
      </div>
    );
  }

  if (perm) {
    return (
      <div
        className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 flex items-start gap-2"
        data-testid="funding-preview-perm-denied"
      >
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>You don&rsquo;t have permission to preview participant funding.</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 flex items-center justify-between gap-2"
        data-testid="funding-preview-error"
      >
        <span className="truncate">Funding preview unavailable: {error}</span>
        <button
          type="button"
          onClick={fetchPreview}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] hover:bg-slate-100"
          data-testid="funding-preview-retry"
        >
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }

  // No data → render nothing (e.g. timesheet missing participant or amount).
  if (!data) return null;
  const estimated = Number(data.estimated_amount || 0);
  if (estimated <= 0 && !data.warning) return null;

  // OK state — small green pill (gives positive feedback to approvers).
  if (!data.warning) {
    const snap = data.snapshot || {};
    const remaining = snap.available_budget;
    const utilisation = snap.utilisation_percent;
    return (
      <div
        className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs flex items-start gap-2"
        data-testid="funding-preview-ok"
      >
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-emerald-700" />
        <div className="flex-1 leading-snug">
          <div className="text-emerald-900">
            <span className="font-semibold">Funding healthy.</span>{' '}
            Approving this timesheet will deduct{' '}
            <span className="font-mono font-semibold">{fmtMoney(estimated)}</span>.
          </div>
          {(remaining != null || utilisation != null) && (
            <div className="text-emerald-800/80 text-[11px] mt-0.5 flex flex-wrap gap-x-3">
              {remaining != null && <span>Available: <span className="font-mono">{fmtMoney(remaining)}</span></span>}
              {utilisation != null && <span>Utilisation: <span className="font-mono">{fmtPct(utilisation)}</span></span>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Warning state — amber banner with full breakdown.
  const snap = data.snapshot || {};
  const balance = snap.available_budget;
  const total = snap.total_budget;
  const utilisation = snap.utilisation_percent;
  const after = balance != null ? balance - estimated : null;

  return (
    <div
      role="alert"
      className="rounded-md border-2 border-amber-300 bg-amber-50 px-3 py-2.5 text-xs flex items-start gap-2.5"
      data-testid="funding-preview-warning"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
      <div className="flex-1 space-y-1.5">
        <div className="text-amber-900">
          <span className="font-semibold">Funding warning.</span>{' '}
          {data.reason || 'Approving may exceed the participant\u2019s remaining funding.'}
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-amber-900/90">
          {snap.category && (
            <div>
              <span className="text-amber-800/70">Category:</span>{' '}
              <span className="font-mono">{snap.category}</span>
            </div>
          )}
          {total != null && (
            <div>
              <span className="text-amber-800/70">Total budget:</span>{' '}
              <span className="font-mono">{fmtMoney(total)}</span>
            </div>
          )}
          <div>
            <span className="text-amber-800/70">Available now:</span>{' '}
            <span className="font-mono font-semibold">{fmtMoney(balance)}</span>
          </div>
          <div>
            <span className="text-amber-800/70">Projected deduction:</span>{' '}
            <span className="font-mono font-semibold">{fmtMoney(estimated)}</span>
          </div>
          <div>
            <span className="text-amber-800/70">Remaining after:</span>{' '}
            <span className={`font-mono font-semibold ${after != null && after < 0 ? 'text-red-700' : 'text-amber-900'}`}>
              {fmtMoney(after)}
            </span>
          </div>
          {utilisation != null && (
            <div>
              <span className="text-amber-800/70">Utilisation:</span>{' '}
              <span className="font-mono">{fmtPct(utilisation)}</span>
            </div>
          )}
        </div>
        <div className="text-[10px] text-amber-800/80 italic">
          Soft warning — approval is not blocked. The hard funding-cap check still runs server-side.
        </div>
      </div>
    </div>
  );
}
